import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { coreRunRows, compositeTrace } from '../../coreOrientation';
import { prefersDip } from '../../anglePreference';
import { formatDateTimeShort } from '../../i18n/format';
import { CoreOrientation3DChart } from './charts/CoreOrientation3DChart';
import { holeTrace, coreSections, tubesFor } from '../../coreRuns3D';

// The composite's colour: the first slot of the Charts tab palette.
const COMPOSITE_COLOUR = '#2a78d6';

// Status colours, Bootstrap's own - kept outside the brand palette, as the
// branding guide asks of status colours. A run with no stored verdict is grey:
// a missing verdict is not a pass.
const CORE_COLOURS = {
    pass: '#198754',
    fail: '#dc3545',
    rejected: '#dc3545',
    unchecked: '#6c757d'
};
const HOLE_COLOUR = '#adb5bd';

// Radii as a fraction of the plotted range (see CoreOrientation3DChart): the
// hole wide enough to read as a hole, the cores visibly inside it.
const HOLE_RADIUS = 0.012;
const CORE_RADIUS = HOLE_RADIUS * 0.6;

const LEGEND = [
    { colour: CORE_COLOURS.pass, key: 'coreOrientation.legendPass' },
    { colour: CORE_COLOURS.fail, key: 'coreOrientation.legendBad' },
    { colour: CORE_COLOURS.unchecked, key: 'coreOrientation.legendUnchecked' },
    { colour: HOLE_COLOUR, key: 'coreOrientation.legendHole' }
];

// Every approved core orientation shot on the hole, deepest last, each linking
// to its own survey in the Table tab for the detail, with the composite run in
// 3D below. Only offered by DrillholeSummary when the hole's summary counts at
// least one CoreTrak run.
export const CoreOrientationTab = ({ drillhole, industry, onOpenSurvey }) => {
    const { t } = useTranslation();
    // One fetch feeds both the table and the chart.
    const [composite, setComposite] = useState(null);
    const rows = useMemo(() => (composite ? coreRunRows(composite) : null), [composite]);
    // Named at render time so the legend follows a language change; memoised on
    // the name so the chart is not redrawn on unrelated renders.
    const compositeName = t('charts.coreTrakComposite');
    const threeDSeries = useMemo(() => {
        const points = compositeTrace(composite);
        return points.length > 1
            ? [{ name: compositeName, color: COMPOSITE_COLOUR, points }]
            : [];
    }, [composite, compositeName]);
    // The hole as a translucent tube with each core run a solid cylinder inside
    // it. Cores first and the hole last, so the hole is drawn over them.
    const tubes = useMemo(() => {
        if (!composite) return [];
        const pieces = tubesFor(holeTrace(composite), coreSections(composite));
        const cores = pieces.filter(p => p.kind === 'core').map(p => ({
            from: p.from, to: p.to, radiusFraction: CORE_RADIUS, color: CORE_COLOURS[p.status], opacity: 1
        }));
        const hole = pieces.filter(p => p.kind === 'hole').map(p => ({
            from: p.from, to: p.to, radiusFraction: HOLE_RADIUS, color: HOLE_COLOUR, opacity: 0.25
        }));
        return [...cores, ...hole];
    }, [composite]);
    const [failed, setFailed] = useState(false);
    const [angleDisplay, setAngleDisplay] = useState(null);
    // Dip or Inclination by the customer's setting, as in every other survey table.
    const showDip = prefersDip(angleDisplay, industry);

    useEffect(() => {
        fetch('api/identity/customersettings')
            .then(response => (response.ok ? response.json() : null))
            .then(settings => setAngleDisplay(settings?.angleDisplay || null))
            .catch(() => { });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setComposite(null);
        setFailed(false);
        // Every survey on the hole is sent: the server keeps only the CoreTrak
        // runs, which is the one place that decision is made.
        const fileIds = (drillhole.surveys || []).map(s => s.fileId);
        fetch('api/surveys/composite', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileIds })
        })
            .then(response => (response.ok ? response.json() : Promise.reject(response)))
            .then(data => { if (!cancelled) setComposite(data); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [drillhole]);

    if (failed) return <p className="text-danger">{t('coreOrientation.loadFailed')}</p>;
    if (rows == null) return <p><em>{t('coreOrientation.loading')}</em></p>;
    if (rows.length === 0) return <p className="text-muted">{t('coreOrientation.none')}</p>;

    const fixed = (value) => (value == null ? '' : value.toFixed(2));

    return (
        <>
            <table className="table table-striped table-sm">
                <thead>
                    <tr>
                        <th>{t('survey.depth')}</th>
                        <th>{t(showDip ? 'survey.dip' : 'survey.inclination')}</th>
                        <th>{t('survey.azimuth')}</th>
                        <th>QA</th>
                        <th>{t('coreOrientation.survey')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => {
                        // A rejected run is greyed - it is no part of the trajectory -
                        // and labelled, so it is not read as a measurement in use.
                        const muted = row.rejected ? 'text-muted' : undefined;
                        return (
                            <tr key={`${row.fileId}-${index}`}>
                                <td className={muted}>{fixed(row.depth)}</td>
                                <td className={muted}>{fixed(row.inclination == null ? null : (showDip ? row.inclination - 90 : row.inclination))}</td>
                                <td className={muted}>{fixed(row.azimuth)}</td>
                                {/* Blank when the shot carries no verdict, rather than a guess. */}
                                <td>{row.qaPass == null ? '' : (
                                    <span style={{ color: row.qaPass ? 'green' : 'red' }}>{row.qaPass ? '✔' : '✘'}</span>
                                )}</td>
                                <td>
                                    {/* When the run was taken, rather than the long survey name,
                                        which stays in the tooltip. The name only when there is no
                                        usable timestamp. */}
                                    <button type="button" className="btn btn-link btn-sm p-0 text-start"
                                        title={`${row.surveyName || row.fileId} - ${t('coreOrientation.openSurvey')}`}
                                        onClick={() => onOpenSurvey?.(row.fileId)}>
                                        {formatDateTimeShort(row.timestamp) || row.surveyName || row.fileId}
                                    </button>
                                    {row.rejected && <span className="text-danger small ms-2">{t('coreOrientation.rejected')}</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {threeDSeries.length > 0 &&
                <CoreOrientation3DChart title={t('charts.threeD')} xAxisName={t('charts.east')}
                    yAxisName={t('charts.north')} zAxisName={t('charts.tvd')} seriesCollection={threeDSeries}
                    tubes={tubes} />
            }
            {threeDSeries.length > 0 &&
                <div className="d-flex flex-wrap justify-content-center small text-muted" style={{ columnGap: 16, rowGap: 4 }}>
                    {LEGEND.map(item => (
                        <span key={item.key} className="d-flex align-items-center">
                            <span aria-hidden="true" style={{
                                display: 'inline-block', width: 12, height: 12, borderRadius: 2,
                                marginRight: 6, backgroundColor: item.colour
                            }} />
                            {t(item.key)}
                        </span>
                    ))}
                </div>
            }
        </>
    );
};
